/**
 * What search engines and link previews show for each public page. The server writes it into the
 * page's head (apps/api/src/lib/seo.ts); the app keeps the same title while the page is open.
 */
import { APP_NAME } from "./constants.js";
import { PLANS, STUDENT_DISCOUNT } from "./plans.js";

export interface PageMeta {
  title: string;
  description: string;
  /** The address search engines should list for this page (another page when it shows the same thing). */
  canonical: string | null;
  /** false: search engines should not list it (sign-in steps, pages of the app). */
  index: boolean;
}

export const SITE_DESCRIPTION =
  "Notes, papers, literature reviews and manuscripts in one place, with AI that shows its sources and no AI jargon to learn. Free up to 1 GB, no card needed.";

/** Picture shown when a link to BrainLog is shared (1200×630, in apps/web/public). */
export const SHARE_IMAGE = { path: "/og-image.png", width: 1200, height: 630, alt: "BrainLog: your research, in one calm place" };

const monthly = (id: string) => PLANS.find((p) => p.id === id)?.monthly;

const PAGES: Record<string, PageMeta> = {
  "/": { title: `${APP_NAME}: research notebook with AI that cites its sources`, description: SITE_DESCRIPTION, canonical: "/", index: true },
  "/pricing": {
    title: `Pricing · ${APP_NAME}`,
    description: `Free up to 1 GB. Storage €${monthly("storage")}, Researcher €${monthly("researcher")} and Researcher Plus €${monthly("researcher_plus")} a month, VAT included, with AI included on the Researcher plans. Students: Researcher for €${STUDENT_DISCOUNT.yearly} a year.`,
    canonical: "/",
    index: true,
  },
  "/signup": { title: `Create your account · ${APP_NAME}`, description: "Start free with 1 GB for your notes, papers and files. No card needed.", canonical: "/signup", index: true },
  "/privacy": {
    title: `Privacy policy · ${APP_NAME}`,
    description: "How BrainLog protects your research: what is stored, what AI services see, encryption, and your rights.",
    canonical: "/privacy",
    index: true,
  },
  "/terms": { title: `Terms of use · ${APP_NAME}`, description: "The terms for using BrainLog, the research notebook: plans, payments, your content and your data.", canonical: "/terms", index: true },
  "/login": { title: `Sign in · ${APP_NAME}`, description: SITE_DESCRIPTION, canonical: null, index: false },
  "/forgot-password": { title: `Forgot your password · ${APP_NAME}`, description: SITE_DESCRIPTION, canonical: null, index: false },
  "/reset-password": { title: `New password · ${APP_NAME}`, description: SITE_DESCRIPTION, canonical: null, index: false },
  "/verify-email": { title: `Confirm your email · ${APP_NAME}`, description: SITE_DESCRIPTION, canonical: null, index: false },
};

/** Public pages search engines may list (for the sitemap). */
export const INDEXED_PAGES = Object.entries(PAGES)
  .filter(([path, m]) => m.index && m.canonical === path)
  .map(([path]) => path);

export const isPublicPage = (path: string) => Object.hasOwn(PAGES, path);

/** Any other address is a page of the app (or does not exist): not listed. */
export const pageMeta = (path: string): PageMeta => PAGES[path] ?? { title: APP_NAME, description: SITE_DESCRIPTION, canonical: null, index: false };
